/**
 * PuppetCreateForm Component (Task 5.4)
 *
 * Inline form for creating a new puppet.
 * Appears when "Add Puppet" button is clicked.
 *
 * Features:
 * - Name input field (required)
 * - Model path input field (optional)
 * - Optional: Clone from existing puppet
 * - Create and Cancel buttons
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Check, Copy } from 'lucide-react';
import { usePuppetStore, type Puppet } from '@/stores/puppetStore';

interface PuppetCreateFormProps {
  /**
   * Callback when form is cancelled
   */
  onCancel: () => void;

  /**
   * Callback when puppet is created successfully
   * @param puppetId - The ID of the newly created puppet
   */
  onCreated: (puppetId: string) => void;
}

export function PuppetCreateForm({
  onCancel,
  onCreated,
}: PuppetCreateFormProps) {
  const puppets = usePuppetStore((state) => state.puppets);
  const createPuppet = usePuppetStore((state) => state.createPuppet);
  const getPuppetById = usePuppetStore((state) => state.getPuppetById);

  const [name, setName] = useState('');
  const [modelPath, setModelPath] = useState('');
  const [cloneFromId, setCloneFromId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  // Focus name input on mount
  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate name
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    // Create puppet with optional tools cloned from another puppet
    let tools: Puppet['tools'] = [];

    if (cloneFromId) {
      const sourcePuppet = getPuppetById(cloneFromId);
      if (sourcePuppet) {
        // Clone tools but mark them as not prebuilt
        tools = sourcePuppet.tools.map((tool) => ({
          ...tool,
          id: `tool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          isPrebuilt: false,
        }));
      }
    }

    const puppetId = createPuppet({
      name: name.trim(),
      modelPath: modelPath.trim(),
      tools,
    });

    onCreated(puppetId);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      className="space-y-3 rounded-md border p-3"
      style={{
        backgroundColor: 'var(--color-dark-bg)',
        borderColor: 'var(--color-dark-border)',
      }}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.15 }}
    >
      <div
        className="text-xs font-medium"
        style={{ color: 'var(--color-dark-text)' }}
      >
        New Puppet
      </div>

      {/* Name field */}
      <div>
        <input
          ref={nameInputRef}
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          className="w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
          style={{
            backgroundColor: 'var(--color-dark-panel)',
            borderColor: error
              ? 'var(--color-body-control)'
              : 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          placeholder="Puppet name *"
          aria-label="Puppet name"
          aria-invalid={!!error}
        />
        {error && (
          <p
            className="mt-1 text-xs"
            style={{ color: 'var(--color-body-control)' }}
          >
            {error}
          </p>
        )}
      </div>

      {/* Model path field */}
      <div>
        <input
          type="text"
          value={modelPath}
          onChange={(e) => setModelPath(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
          style={{
            backgroundColor: 'var(--color-dark-panel)',
            borderColor: 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          placeholder="Model path (optional)"
          aria-label="Model path"
        />
      </div>

      {/* Clone from existing puppet */}
      {puppets.length > 0 && (
        <div>
          <label
            className="mb-1 flex items-center gap-1 text-xs"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            <Copy className="h-3 w-3" />
            Clone tools from
          </label>
          <select
            value={cloneFromId}
            onChange={(e) => setCloneFromId(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-panel)',
              borderColor: 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
          >
            <option value="">None (start with empty tools)</option>
            {puppets.map((puppet) => (
              <option key={puppet.id} value={puppet.id}>
                {puppet.name} ({puppet.tools.length} tools)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-end gap-2 pt-1">
        <motion.button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
          style={{
            backgroundColor: 'transparent',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <X className="h-3 w-3" />
          Cancel
        </motion.button>

        <motion.button
          type="submit"
          className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium"
          style={{
            backgroundColor: 'var(--color-body-head)',
            color: 'white',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Check className="h-3 w-3" />
          Create
        </motion.button>
      </div>
    </motion.form>
  );
}
